import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, InputLabel, Select, FormControl, Checkbox } from '@mui/material';
import axios from 'axios';
const BookingChat = lazy(() => import('../components/BookingChat.jsx'));
import EnhancedErrorBoundary from '../components/EnhancedErrorBoundary.jsx';
import { BookingCardSkeleton, TableRowSkeleton } from '../components/SkeletonLoaders.jsx';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, Grid } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Pagination from '@mui/material/Pagination';

const statusOptions = [
  '', 'pending', 'confirmed', 'in_progress', 'awaiting_customer_confirmation', 'completed', 'cancelled'
];

const sortOptions = [
  { value: 'createdAt_desc', label: 'Newest' },
  { value: 'createdAt_asc', label: 'Oldest' },
  { value: 'amount_desc', label: 'Amount (High to Low)' },
  { value: 'amount_asc', label: 'Amount (Low to High)' },
];

// Pagination defaults
const DEFAULT_BOOKINGS_PER_PAGE = 10;
const PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

export default function MoverBookings({ token }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [sort, setSort] = useState('createdAt_desc');
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [page, setPage] = useState(1);
  const [bookingsPerPage, setBookingsPerPage] = useState(DEFAULT_BOOKINGS_PER_PAGE);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [bulkActionStatus, setBulkActionStatus] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exceptionOpen, setExceptionOpen] = useState(false);
  const [exceptionReason, setExceptionReason] = useState('');
  const [exceptionAmount, setExceptionAmount] = useState('');

  useEffect(() => {
    const fetchBookings = async () => {
      setLoading(true);
      try {
        const res = await axios.get('/api/bookings?mover=me', { withCredentials: true });
        setBookings(res.data || []);
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to load bookings.', severity: 'error' });
        console.error('Error loading bookings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [token]);

  useEffect(() => {
    let filtered = [...bookings];
    // General search (job #, customer name, email, origin, destination, status, amount, date)
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(b => {
        const job = b.jobNumber || '';
        const name = b.customerName || b.name || '';
        const email = b.customerEmail || b.email || '';
        const origin = b.origin || '';
        const dest = b.destination || '';
        const stat = b.status || '';
        const amt = b.amount ? b.amount.toString() : '';
        const date = b.moveDate ? dayjs(b.moveDate).format('YYYY-MM-DD') : '';
        return [job, name, email, origin, dest, stat, amt, date].some(field => field.toLowerCase().includes(s));
      });
    }
    // Status filter
    if (status) filtered = filtered.filter(b => b.status === status);
    // Amount filter
    if (minAmount) filtered = filtered.filter(b => b.amount >= parseFloat(minAmount));
    if (maxAmount) filtered = filtered.filter(b => b.amount <= parseFloat(maxAmount));
    // Date range filter
    if (dateFrom) filtered = filtered.filter(b => dayjs(b.moveDate).isAfter(dayjs(dateFrom).subtract(1, 'day')));
    if (dateTo) filtered = filtered.filter(b => dayjs(b.moveDate).isBefore(dayjs(dateTo).add(1, 'day')));
    // Sorting
    filtered.sort((a, b) => {
      switch (sort) {
        case 'createdAt_asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'createdAt_desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'amount_asc':
          return (a.amount || 0) - (b.amount || 0);
        case 'amount_desc':
          return (b.amount || 0) - (a.amount || 0);
        default:
          return 0;
      }
    });
    setFilteredBookings(filtered);
  }, [bookings, search, status, minAmount, maxAmount, dateFrom, dateTo, sort]);

  const handleRowClick = (booking) => setSelected(booking);
  const handleClose = () => setSelected(null);

  const handleAction = async (status) => {
    setActionLoading(true);
    try {
      await axios.put(`/api/bookings/${selected._id}/status`, { status }, { withCredentials: true });
      setSnackbar({ open: true, message: `Booking marked as ${status}.`, severity: 'success' });
      const resultingStatus = status === 'completed' ? 'awaiting_customer_confirmation' : status;
      setSelected({ ...selected, status: resultingStatus });
      setBookings(bks => bks.map(b => b._id === selected._id ? { ...b, status: resultingStatus } : b));
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update status.', severity: 'error' });
      console.error('Error updating booking status:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const submitCostException = async () => {
    if (!selected?._id || !exceptionReason.trim() || Number(exceptionAmount) <= 0) return;
    setActionLoading(true);
    try {
      await axios.post(`/api/bookings/${selected._id}/cost-exceptions`, {
        reason: exceptionReason.trim(),
        claimedAmount: Number(exceptionAmount),
        category: 'OTHER',
        customerCaused: false,
      }, { withCredentials: true });
      setExceptionOpen(false);
      setExceptionReason('');
      setExceptionAmount('');
      setSnackbar({ open: true, message: 'Cost exception submitted for admin review.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to submit cost exception.', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk actions
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedBookings(paginatedBookings.map(b => b._id));
    } else {
      setSelectedBookings([]);
    }
  };

  const handleSelectBooking = (bookingId) => {
    setSelectedBookings(prev => 
      prev.includes(bookingId) 
        ? prev.filter(id => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedBookings.length === 0 || !bulkActionStatus) return;
    
    setActionLoading(true);
    try {
      await axios.put('/api/bookings/bulk-status', {
        bookingIds: selectedBookings,
        status: bulkActionStatus
      }, { withCredentials: true });
      
      setSnackbar({ 
        open: true, 
        message: `Updated ${selectedBookings.length} bookings to ${bulkActionStatus}`, 
        severity: 'success' 
      });
      
      // Update local state
      setBookings(bks => bks.map(b => 
        selectedBookings.includes(b._id) ? { ...b, status: bulkActionStatus } : b
      ));
      
      setSelectedBookings([]);
      setBulkActionStatus('');
    } catch {
      setSnackbar({ open: true, message: 'Failed to update bookings.', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get('/api/analytics/export/bookings', {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bookings-export-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSnackbar({ open: true, message: 'Bookings exported successfully!', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to export bookings.', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const pageCount = Math.ceil(filteredBookings.length / bookingsPerPage);
  const paginatedBookings = filteredBookings.slice((page - 1) * bookingsPerPage, page * bookingsPerPage);

  // Show skeleton while loading
  if (loading) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: { xs: 1, md: 3 } }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Bookings</Typography>
        {/* Show skeleton table rows */}
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
          {[...Array(5)].map((_, i) => (
            <TableRowSkeleton key={i} />
          ))}
        </Paper>
      </Box>
    );
  }

  return (
    <EnhancedErrorBoundary>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ 
          width: '100%',
          height: '100%',
          p: { xs: 2, md: 3 }, 
          bgcolor: 'background.default',
          overflow: 'auto'
        }}>
          {/* Content wrapper with max width and centering */}
          <Box sx={{
            maxWidth: 1400,
            mx: 'auto',
            width: '100%'
          }}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Bookings</Typography>
        {/* Filters */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                label="Status"
                value={status}
                onChange={e => setStatus(e.target.value)}
                sx={{ minWidth: 140 }} // Make dropdown wide enough for field name
              >
                {statusOptions.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt ? opt[0].toUpperCase() + opt.slice(1) : 'All'}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Min Amount"
                type="number"
                value={minAmount}
                onChange={e => setMinAmount(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Max Amount"
                type="number"
                value={maxAmount}
                onChange={e => setMaxAmount(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <DatePicker
                label="From"
                value={dateFrom}
                onChange={setDateFrom}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <DatePicker
                label="To"
                value={dateTo}
                onChange={setDateTo}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                label="Sort By"
                value={sort}
                onChange={e => setSort(e.target.value)}
                sx={{ minWidth: 170 }} // Make sort dropdown wide enough for label
              >
                {sortOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button variant="outlined" color="secondary" fullWidth onClick={() => {
                setSearch(''); setStatus(''); setMinAmount(''); setMaxAmount(''); setDateFrom(null); setDateTo(null); setSort('createdAt_desc');
              }}>Reset</Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Bulk Actions Bar */}
        {selectedBookings.length > 0 && (
          <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body1" fontWeight={600}>
                {selectedBookings.length} selected
              </Typography>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Bulk Action</InputLabel>
                <Select
                  value={bulkActionStatus}
                  onChange={(e) => setBulkActionStatus(e.target.value)}
                  label="Bulk Action"
                >
                  {statusOptions.filter(s => s).map(opt => (
                    <MenuItem key={opt} value={opt}>
                      {opt[0].toUpperCase() + opt.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button 
                variant="contained" 
                onClick={handleBulkStatusUpdate}
                disabled={!bulkActionStatus || actionLoading}
              >
                {actionLoading ? 'Updating...' : 'Apply'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => setSelectedBookings([])}
              >
                Clear Selection
              </Button>
            </Box>
          </Paper>
        )}

        {/* Export Button */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="outlined" 
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export to CSV'}
          </Button>
        </Box>

        {/* Bookings Table */}
        {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 6 }} /> : (
          <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={paginatedBookings.length > 0 && selectedBookings.length === paginatedBookings.length}
                      indeterminate={selectedBookings.length > 0 && selectedBookings.length < paginatedBookings.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Origin</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Destination</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 6 }}>
                      No bookings found.
                    </TableCell>
                  </TableRow>
                ) : paginatedBookings.map(b => (
                  <TableRow key={b._id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedBookings.includes(b._id)}
                        onChange={() => handleSelectBooking(b._id)}
                      />
                    </TableCell>
                    <TableCell onClick={() => handleRowClick(b)} style={{ cursor: 'pointer' }}>
                      {b.moveDate ? new Date(b.moveDate).toLocaleDateString() : ''}
                    </TableCell>
                    <TableCell onClick={() => handleRowClick(b)} style={{ cursor: 'pointer' }}>
                      {b.contactName || b.user?.name || ''}
                    </TableCell>
                    <TableCell onClick={() => handleRowClick(b)} style={{ cursor: 'pointer' }}>
                      {b.origin}
                    </TableCell>
                    <TableCell onClick={() => handleRowClick(b)} style={{ cursor: 'pointer' }}>
                      {b.destination}
                    </TableCell>
                    <TableCell onClick={() => handleRowClick(b)} style={{ cursor: 'pointer' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 600, color: b.status === 'completed' ? 'success.main' : b.status === 'cancelled' ? 'error.main' : 'primary.main' }}>{b.status.replace('_', ' ').toUpperCase()}</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Dialog open={!!selected} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: 0.5 }}>Booking Details</DialogTitle>
          <DialogContent dividers sx={{ bgcolor: 'background.default', p: { xs: 2, sm: 3 } }}>
            {selected && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography><b>Date:</b> {selected.moveDate ? new Date(selected.moveDate).toLocaleString() : ''}</Typography>
                <Typography><b>Customer:</b> {selected.contactName || selected.user?.name || ''}</Typography>
                <Typography><b>Email:</b> {selected.contactEmail || selected.user?.email || ''}</Typography>
                <Typography><b>Phone:</b> {selected.contactPhone || selected.user?.phone || ''}</Typography>
                <Typography><b>Origin:</b> {selected.origin}</Typography>
                <Typography><b>Destination:</b> {selected.destination}</Typography>
                <Typography><b>Status:</b> {selected.status}</Typography>
                <Typography><b>Special Instructions:</b> {selected.specialRequirements}</Typography>
                <Typography><b>Price:</b> ${selected.price}</Typography>
                <Typography><b>Inventory:</b> {Array.isArray(selected.inventory) ? selected.inventory.join(', ') : ''}</Typography>
                <Button variant="outlined" color="warning" onClick={() => setExceptionOpen(true)} disabled={actionLoading} sx={{ alignSelf: 'flex-start', mt: 1 }}>Submit cost exception</Button>
                <Box sx={{ mt: 2 }}>
                  <Suspense fallback={<CircularProgress size={24} />}>
                    <BookingChat bookingId={selected._id} viewerRole="mover" />
                  </Suspense>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ bgcolor: 'background.default', p: 2 }}>
            <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
            {selected?.status === 'pending' && (
              <Button onClick={() => handleAction('confirmed')} disabled={actionLoading} variant="contained" color="primary" sx={{ borderRadius: 2 }}>Accept booking</Button>
            )}
            {selected?.status === 'confirmed' && (
              <Button onClick={() => handleAction('in_progress')} disabled={actionLoading} variant="contained" color="primary" sx={{ borderRadius: 2 }}>Start move</Button>
            )}
            {selected?.status === 'in_progress' && (
              <Button onClick={() => handleAction('completed')} disabled={actionLoading} variant="contained" color="success" sx={{ borderRadius: 2 }}>Complete</Button>
            )}
            {selected && ['pending', 'confirmed', 'in_progress'].includes(selected.status) && (
              <Button onClick={() => handleAction('cancelled')} color="error" disabled={actionLoading} variant="contained" sx={{ borderRadius: 2 }}>Cancel</Button>
            )}
          </DialogActions>
        </Dialog>
        <Dialog open={exceptionOpen} onClose={() => setExceptionOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Submit Cost Exception</DialogTitle>
          <DialogContent dividers>
            <TextField fullWidth type="number" label="Additional amount (CAD)" value={exceptionAmount} onChange={e => setExceptionAmount(e.target.value)} sx={{ mb: 2 }} inputProps={{ min: 0.01, step: 0.01 }} />
            <TextField fullWidth multiline minRows={4} label="Reason and supporting details" value={exceptionReason} onChange={e => setExceptionReason(e.target.value)} />
          </DialogContent>
          <DialogActions><Button onClick={() => setExceptionOpen(false)}>Cancel</Button><Button variant="contained" color="warning" onClick={submitCostException} disabled={actionLoading || !exceptionReason.trim() || Number(exceptionAmount) <= 0}>Submit for review</Button></DialogActions>
        </Dialog>
        <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
        </Snackbar>
        {/* Per Page Selector and Pagination Controls */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, flexWrap: 'wrap', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="bookings-per-page-label">Rows per page</InputLabel>
            <Select
              labelId="bookings-per-page-label"
              value={bookingsPerPage}
              label="Rows per page"
              onChange={e => { setBookingsPerPage(Number(e.target.value)); setPage(1); }}
            >
              {PER_PAGE_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
    </EnhancedErrorBoundary>
  );
}
